import React, { useMemo } from 'react';
import styles from './BarChart.module.css';

interface DataPoint {
  label: string;
  value: number;
}

interface BarChartProps {
  data: DataPoint[];
  height?: number;
  color?: string;
  orientation?: 'vertical' | 'horizontal';
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  height = 200,
  color = '#10B981',
  orientation = 'vertical'
}) => {
  const maxValue = useMemo(() => Math.max(...data.map(d => d.value)), [data]);

  const yLabels = useMemo(() => {
    if (orientation === 'horizontal') return [];
    return [0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue].map(v =>
      v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
    ).reverse();
  }, [maxValue, orientation]);

  const xLabels = useMemo(() => {
    if (orientation === 'vertical') return [];
    const max = Math.ceil(maxValue / 5000) * 5000;
    return [0, max * 0.25, max * 0.5, max * 0.75, max].map(v =>
      v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
    );
  }, [maxValue, orientation]);

  if (orientation === 'horizontal') {
    return (
      <div className={styles.horizontalContainer} style={{ minHeight: height }}>
        <div className={styles.horizontalBars}>
          {data.map((d, i) => (
            <div key={i} className={styles.horizontalBarRow}>
              <span className={styles.horizontalLabel}>{d.label}</span>
              <div className={styles.horizontalBarWrapper}>
                <div
                  className={styles.horizontalBar}
                  style={{
                    width: `${(d.value / maxValue) * 100}%`,
                    background: `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.horizontalXAxis}>
          {xLabels.map((label, i) => (
            <span key={i} className={styles.xLabel}>{label}</span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ height }}>
      <div className={styles.yAxis}>
        {yLabels.map((label, i) => (
          <span key={i} className={styles.yLabel}>{label}</span>
        ))}
      </div>
      <div className={styles.chartArea}>
        <div className={styles.barsContainer}>
          {data.map((d, i) => (
            <div key={i} className={styles.barWrapper}>
              <div
                className={styles.bar}
                style={{
                  height: `${(d.value / maxValue) * 100}%`,
                  background: `linear-gradient(180deg, ${color} 0%, ${color}CC 100%)`
                }}
              />
            </div>
          ))}
        </div>
        <div className={styles.xAxis}>
          {data.map((d, i) => (
            <span key={i} className={styles.xLabel}>{d.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};
